/**
 * Groupements d'entreprises (mère / filiale), profondeur 1.
 * Module PUR : aucune dépendance React/Supabase → testable unitairement.
 * Source de vérité serveur : api.enterprises.parent_enterprise_id + is_group_parent,
 * héritage lecture seule via api.my_group_child_enterprise_ids().
 * L'UI ne fait que refléter la RLS : elle ne l'applique pas.
 */

export type GroupStatus = 'independent' | 'parent' | 'child';
export type EnterpriseAccess = 'member' | 'group_read';

export interface GroupFields {
  id: string;
  adm_company?: string | null;
  parent_enterprise_id?: string | null;
  is_group_parent?: boolean | null;
}

export interface AdminScope {
  isSuperAdmin: boolean;
  userEmail: string;
  userEnterpriseId?: string | null;
}

const eq = (a?: string | null, b?: string | null) =>
  !!a && !!b && a.toLowerCase() === b.toLowerCase();

/** Un rattachement à une mère l'emporte toujours sur le drapeau is_group_parent. */
export function groupStatus(e: GroupFields): GroupStatus {
  if (e.parent_enterprise_id) return 'child';
  return e.is_group_parent ? 'parent' : 'independent';
}

export function isReadOnlyAccess(access?: EnterpriseAccess): boolean {
  return access === 'group_read';
}

/**
 * L'entreprise sélectionnée est-elle consultée par héritage de groupe ?
 * Fail-open volontaire : une entreprise inconnue de la liste (course au
 * chargement) ne verrouille pas l'UI — la RLS reste l'autorité.
 */
export function currentEnterpriseIsReadOnly(
  list: Array<{ id: string; access?: EnterpriseAccess }>,
  currentId: string,
): boolean {
  return isReadOnlyAccess(list.find(e => e.id === currentId)?.access);
}

export function childCountByParent<T extends GroupFields>(rows: T[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const r of rows) {
    if (!r.parent_enterprise_id) continue;
    out[r.parent_enterprise_id] = (out[r.parent_enterprise_id] ?? 0) + 1;
  }
  return out;
}

/** Entreprises administrées EN DIRECT (pas par héritage de groupe). */
function directlyAdministered<T extends GroupFields>(rows: T[], scope: AdminScope): Set<string> {
  const ids = new Set<string>();
  for (const r of rows) {
    if (eq(r.adm_company, scope.userEmail)) ids.add(r.id);
    if (scope.userEnterpriseId && r.id === scope.userEnterpriseId) ids.add(r.id);
  }
  return ids;
}

/**
 * Liste visible dans la page Admin : entreprises administrées en direct + leurs
 * filiales. La mère doit être DÉCLARÉE mère (is_group_parent), exactement comme
 * api.my_group_child_enterprise_ids() : sinon l'UI montrerait des filiales que
 * la RLS refuse, et l'écran serait vide sans explication.
 */
export function scopeEnterprisesForAdmin<T extends GroupFields>(rows: T[], scope: AdminScope): T[] {
  if (scope.isSuperAdmin) return rows;
  const own = directlyAdministered(rows, scope);
  // Seule une administration PAR EMAIL active le statut mère : un simple membre
  // dont userEnterpriseId coïncide avec une mère (sans en être admin) ne doit
  // pas ouvrir l'accès aux filiales — sinon toute personne rattachée à une
  // mère verrait les filiales sans en avoir le droit.
  const activeParents = new Set(
    rows.filter(r => eq(r.adm_company, scope.userEmail) && r.is_group_parent === true).map(r => r.id),
  );
  return rows.filter(r =>
    own.has(r.id) || (!!r.parent_enterprise_id && activeParents.has(r.parent_enterprise_id)));
}

/** Une filiale héritée est TOUJOURS en lecture seule (décision D3 de la spec). */
export function canManageEnterprise<T extends GroupFields>(ent: T, scope: AdminScope): boolean {
  if (scope.isSuperAdmin) return true;
  return eq(ent.adm_company, scope.userEmail)
      || (!!scope.userEnterpriseId && ent.id === scope.userEnterpriseId);
}
